import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage, formatDate } from "../Assets/assetsShared";
import "./EmployeeDashboard.css";

const BAR_COLORS = [
  "#383a79",
  "#5878a8",
  "#e8891e",
  "#d94f5c",
  "#7fb8b0",
  "#5fa44a",
  "#e8c93c",
  "#ae73a2",
  "#f595a3",
  "#98725a",
];

const localToday = () => new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD

const buildMonthOptions = () => {
  const now = new Date();

  return Array.from({ length: 12 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

    return {
      value,
      label: date.toLocaleString("en-US", { month: "long", year: "numeric" }),
    };
  });
};

const emptyForm = () => ({
  date: localToday(),
  projectName: "",
  hours: "",
  note: "",
});

export default function EmployeeDashboard({ user }) {
  const navigate = useNavigate();
  const monthOptions = useMemo(buildMonthOptions, []);

  const [month, setMonth] = useState(monthOptions[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  /* ================= LOAD ================= */
  const loadData = async (selectedMonth = month) => {
    try {
      setError("");
      const res = await api.get("/dashboard/employee", {
        params: { month: selectedMonth },
      });
      setData(res.data);
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to load dashboard."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  /* ================= ADD / DELETE HOURS ================= */
  const setField = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const openForm = () => {
    setForm(emptyForm());
    setError("");
    setShowForm(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);
      setError("");

      await api.post("/dashboard/time-entries", {
        ...form,
        hours: Number(form.hours),
      });

      setShowForm(false);

      // show the month of the entry that was just added
      const entryMonth = form.date.slice(0, 7);
      if (monthOptions.some((option) => option.value === entryMonth)) {
        if (entryMonth === month) await loadData(month);
        else setMonth(entryMonth);
      } else {
        await loadData(month);
      }
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to save hours."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Delete ${entry.hours} hrs on ${entry.projectName}?`)) return;

    try {
      setError("");
      await api.delete(`/dashboard/time-entries/${entry._id}`);
      await loadData(month);
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to delete entry."));
    }
  };

  const chart = data?.chart || [];
  const maxHours = chart.length ? chart[0].hours : 0;

  // whose dashboard is this (full name + role)
  const displayName = String(data?.user?.name || user?.name || "").trim();
  const displayRole = String(data?.user?.role || user?.role || "").trim();
  const isAdmin = displayRole.toLowerCase() === "admin";
  const dashboardTitle = isAdmin ? "Admin Dashboard" : "Employee Dashboard";

  return (
    <div className="ed-page">
      {/* ================= TITLE ================= */}
      <div className="ed-title-row">
        <h1>{dashboardTitle}</h1>

        <button
          type="button"
          className="ed-bell"
          title={
            data?.pendingApprovals
              ? `${data.pendingApprovals} leave request(s) waiting`
              : "No new notifications"
          }
          onClick={() => navigate("/leave-application")}
        >
          🔔
          {data?.pendingApprovals > 0 && (
            <span className="ed-bell-count">{data.pendingApprovals}</span>
          )}
        </button>
      </div>

      {error && <div className="ed-alert">{error}</div>}

      {/* ================= GREETING ================= */}
      <h2 className="ed-hello">Hello {displayName},</h2>

      <p className="ed-intro">
        You have been actively with us. Thank you for your dedication and focus on
        the work. The charts below help you analyse your monthly working pattern.
        Your current post is <strong>{displayRole}</strong>.
      </p>

      <div className="ed-toolbar">
        <label className="ed-month">
          <span>Select Month:</span>
          <select value={month} onChange={(event) => setMonth(event.target.value)}>
            {monthOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <button type="button" className="ed-btn" onClick={openForm}>
          + Add Hours
        </button>
      </div>

      {loading && <div className="ed-empty">Loading...</div>}

      {!loading && data && (
        <>
          {/* ================= STAT CARDS ================= */}
          <div className="ed-stats">
            <div className="ed-stat">
              <span>Total Hours</span>
              <strong>{data.totalHours.toFixed(2)} hrs</strong>
            </div>

            <div className="ed-stat">
              <span>Projects</span>
              <strong>{data.projectCount}</strong>
            </div>

            <div className="ed-stat">
              <span>Leaves</span>
              <strong>{data.leaveDays}</strong>
            </div>
          </div>

          {/* ================= CHART ================= */}
          <h3 className="ed-section">Project-wise Hours Spent</h3>

          {!chart.length ? (
            <div className="ed-empty">
              Is mahine ke koi hours add nahi hue. "+ Add Hours" se apne hours add karo.
            </div>
          ) : (
            <div className="ed-chart">
              {chart.map((row, index) => (
                <div key={row.project} className="ed-chart-row">
                  <div className="ed-chart-label" title={row.project}>
                    {row.project}
                  </div>

                  <div className="ed-chart-track">
                    <div
                      className="ed-chart-bar"
                      style={{
                        width: `${Math.max(2, (row.hours / maxHours) * 100)}%`,
                        background: BAR_COLORS[index % BAR_COLORS.length],
                      }}
                    />
                    <span className="ed-chart-value">{row.hours}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ================= ENTRIES ================= */}
          {data.entries.length > 0 && (
            <>
              <h3 className="ed-section">My Hour Entries</h3>

              <div className="ed-table-wrap">
                <table className="ed-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Project</th>
                      <th>Hours</th>
                      <th>Note</th>
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {data.entries.map((entry) => (
                      <tr key={entry._id}>
                        <td>{formatDate(entry.date)}</td>
                        <td>{entry.projectName}</td>
                        <td>{entry.hours}</td>
                        <td>{entry.note || "-"}</td>
                        <td>
                          <button
                            type="button"
                            className="ed-delete"
                            onClick={() => handleDelete(entry)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* ================= ADD HOURS DRAWER ================= */}
      {showForm && (
        <div className="ed-overlay">
          <div className="ed-drawer">
            <div className="ed-drawer-head">
              <h3>Add Hours</h3>

              <button type="button" className="ed-close" onClick={() => setShowForm(false)}>
                ✖
              </button>
            </div>

            {error && <div className="ed-alert">{error}</div>}

            <form className="ed-form" onSubmit={handleSave}>
              <label className="ed-field">
                <span>Date *</span>
                <input
                  required
                  type="date"
                  max={localToday()}
                  value={form.date}
                  onChange={(e) => setField("date", e.target.value)}
                />
              </label>

              <label className="ed-field">
                <span>Project Name *</span>
                <input
                  required
                  list="ed-project-list"
                  placeholder="Project ka naam"
                  value={form.projectName}
                  onChange={(e) => setField("projectName", e.target.value)}
                />
                <datalist id="ed-project-list">
                  {(data?.suggestions || []).map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
              </label>

              <label className="ed-field">
                <span>Hours *</span>
                <input
                  required
                  type="number"
                  min="0.25"
                  max="24"
                  step="0.25"
                  placeholder="e.g. 2.5"
                  value={form.hours}
                  onChange={(e) => setField("hours", e.target.value)}
                />
              </label>

              <label className="ed-field">
                <span>Note</span>
                <textarea
                  rows="3"
                  value={form.note}
                  onChange={(e) => setField("note", e.target.value)}
                />
              </label>

              <div className="ed-footer">
                <button type="submit" className="ed-btn" disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>

                <button
                  type="button"
                  className="ed-btn ed-btn-light"
                  onClick={() => setShowForm(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}